import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import CircleIcon from '@mui/icons-material/Circle';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';

import { Button } from '~common/components';
import { config } from '~common/utils';
import useStyles from './styles';

const SessionsCard = ({
  session, setAppointmentFormVisible, onEditingAppointmentChange, userType,
}) => {
  const { UserType } = config;
  const {
    title, startDate, endDate, color, callDetails, subject: { subjectName },
  } = session;
  const { classes } = useStyles({ color });
  const isParent = userType === UserType.PARENT;
  const learner = session.learner?.fullName;
  const attendeeName = userType === UserType.TUTOR
    ? learner : session.tutor?.fullName;

  const time = startDate && endDate
    && `${dayjs(startDate).format('h:mm')} - ${dayjs(endDate).format('h:mm')}`;
  const date = startDate && dayjs(startDate).format('dddd, D MMMM');

  const getMeetingLinkInfo = (text) => {
    if (!text) {
      return null;
    }
    const urlRegex = /(https?:\/\/[^\s]+)/;
    const match = text.match(urlRegex);
    if (!match) {
      return null;
    }

    const url = match[0];
    let platform = 'Meeting';
    let iconUrl = null;
    let iconHeight = 20;

    const platforms = [
      {
        name: 'Google Meet', regex: /meet\.google\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/9/9b/Google_Meet_icon_%282020%29.svg', iconHeight: 20,
      },
      {
        name: 'Zoom', regex: /zoom\.us/, iconUrl: 'https://www.logo.wine/a/logo/Zoom_Video_Communications/Zoom_Video_Communications-Logo.wine.svg', iconHeight: 30,
      },
      {
        name: 'Microsoft Teams', regex: /teams\.microsoft\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg', iconHeight: 20,
      },
      {
        name: 'Skype', regex: /join\.skype\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/6/60/Skype_logo_%282019%E2%80%93present%29.svg', iconHeight: 20,
      },
      {
        name: 'Webex', regex: /webex\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/0d/Cisco_Webex_logo.svg', iconHeight: 20,
      },
      {
        name: 'Discord', regex: /discord\.gg/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Discord_Color_Icon.svg', iconHeight: 22,
      },
      {
        name: 'Jitsi Meet', regex: /jitsi\.org|meet\.jit\.si/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/5e/Jitsi_logo.svg', iconHeight: 20,
      },
      {
        name: 'GoToMeeting', regex: /gotomeeting\.com/, iconUrl: 'https://upload.wikimedia.org/wikipedia/commons/e/e8/GoToMeeting_logo.svg', iconHeight: 20,
      },
    ];

    const detectedPlatform = platforms.find((p) => p.regex.test(url));
    if (detectedPlatform) {
      platform = detectedPlatform.name;
      iconUrl = detectedPlatform.iconUrl;
      iconHeight = detectedPlatform.iconHeight;
    }

    return {
      url, platform, iconUrl, iconHeight,
    };
  };

  const renderCallDetailsWithLinks = (text) => {
    if (!text) {
      return null;
    }
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.filter(Boolean).map((part, index) => {
      if (part.startsWith('http')) {
        return (
          <a href={part} key={index} target="_blank" rel="noreferrer">
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const meetingInfo = getMeetingLinkInfo(callDetails);
  const joinButtonText = meetingInfo ? 'Join Session' : '';

  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseFloat(window.getComputedStyle(textRef.current).lineHeight);
      const maxHeight = lineHeight * 3;
      setIsOverflowing(textRef.current.scrollHeight > maxHeight + 1);
    }
  }, [callDetails]);

  return (
    <Grid container className={classes.sessionsCard}>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={6}>
            <Typography variant="body2" className={classes.nameText}>
              <CircleIcon className={classes.circle} />{attendeeName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <div className={classes.icons}>
              <IconButton
                className={classes.editIcon}
                onClick={() => {
                  setAppointmentFormVisible(true);
                  onEditingAppointmentChange(session);
                }}
              >
                {userType === 'tutor' ? <EditIcon /> : <InfoIcon />}
              </IconButton>
            </div>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.cardInfo}>
        <Typography variant="body2" className={classes.dateText}>
          {date} <span className={classes.timeText}>{time}</span>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" className={classes.subjectText}>
          <span>{subjectName}:</span> <span>{title}</span>
        </Typography>
        {isParent && (
          <Typography variant="body2" className={classes.learnerNameText}>
            {learner}
          </Typography>
        )}
        {callDetails && (
          <div style={{ marginTop: '8px' }}>
            <Typography
              ref={textRef}
              variant="body2"
              className={classes.callDetailsText}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: expanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {renderCallDetailsWithLinks(callDetails)}
            </Typography>

            {isOverflowing && (
              <Typography
                variant="body2"
                style={{
                  color: '#1976d2',
                  cursor: 'pointer',
                  marginTop: '4px',
                  marginBottom: '12px',
                  display: 'block',
                  fontWeight: 500,
                }}
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? 'Show less' : 'Show more'}
              </Typography>
            )}

            {meetingInfo && (
              <Button
                variant="outlined"
                href={meetingInfo.url}
                target="_blank"
                rel="noreferrer"
                style={{ marginTop: '0px' }}
                onClick={(e) => e.stopPropagation()}
                endIcon={
                  meetingInfo.iconUrl && (
                    <img
                      src={meetingInfo.iconUrl}
                      alt={`${meetingInfo.platform} logo`}
                      height={meetingInfo.iconHeight}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )
                }
              >
                {joinButtonText}
              </Button>
            )}
          </div>
        )}
      </Grid>
    </Grid>
  );
};

SessionsCard.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    learner: PropTypes.shape({
      fullName: PropTypes.string,
    }),
    tutor: PropTypes.shape({
      fullName: PropTypes.string,
    }),
    color: PropTypes.string,
    callDetails: PropTypes.string,
    subject: PropTypes.shape({
      subjectName: PropTypes.string,
    }),
  }).isRequired,
  setAppointmentFormVisible: PropTypes.func.isRequired,
  onEditingAppointmentChange: PropTypes.func.isRequired,
  userType: PropTypes.oneOf(['admin', 'tutor', 'parent', 'learner']).isRequired,
};

const UpcomingSessions = ({
  data, setAppointmentFormVisible, onEditingAppointmentChange, userType,
}) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.container}>
      <div className={classes.subHeader}>
        <Typography variant="body1" className={classes.title}>
          Upcoming sessions
        </Typography>
        <Button
          className={classes.link}
          variant="text"
          onClick={() => navigate('/lessons')}
        >
          See all
        </Button>
      </div>
      {data.length
        ? data.map((session) => (
          <SessionsCard
            session={session}
            setAppointmentFormVisible={setAppointmentFormVisible}
            onEditingAppointmentChange={onEditingAppointmentChange}
            key={session.id}
            userType={userType}
          />
        )) : (
          <Typography variant="body2" className={classes.noDataText}>
            No upcoming sessions
          </Typography>
        )}
    </div>
  );
};

UpcomingSessions.defaultProps = {
  data: [],
};

UpcomingSessions.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    learner: PropTypes.shape({
      fullName: PropTypes.string,
    }),
    tutor: PropTypes.shape({
      fullName: PropTypes.string,
    }),
    color: PropTypes.string,
    callDetails: PropTypes.string,
  })),
  setAppointmentFormVisible: PropTypes.func.isRequired,
  onEditingAppointmentChange: PropTypes.func.isRequired,
  userType: PropTypes.oneOf(['admin', 'tutor', 'parent', 'learner']).isRequired,
};

export default UpcomingSessions;
